import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";

import "./Eyeq.css";
import { useDispatch, useSelector } from "react-redux";
import { getEyeq } from "../../features/bapateye/Slice";


const getImages = (item) => {
  if (Array.isArray(item?.images)) return item.images.filter(Boolean);
  if (typeof item?.image === "string") {
    return item.image
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const Eyeq = () => {
  const dispatch = useDispatch();
  const bapat_eyeq = useSelector((state) => state.bapateye.eyeqs);

  useEffect(() => {
    dispatch(getEyeq());
  }, []);

  const items = Array.isArray(bapat_eyeq) ? bapat_eyeq : [];

  // Which header button is highlighted (follows scrolling)
  const [activeId, setActiveId] = useState(null);
  const currentId = activeId ?? items[0]?.id ?? null;
  const currentIndex = items.findIndex((i) => i.id === currentId);

  const headerRef = useRef(null);
  const navListRef = useRef(null);
  const sectionRefs = useRef({});
  const scrollLockUntil = useRef(0);
  const [headerHeight, setHeaderHeight] = useState(0);


  useLayoutEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    const raf = requestAnimationFrame(measure);
    return () => {
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(raf);
    };
  }, [items.length]);


  const getLine = () =>
    headerRef.current ? headerRef.current.getBoundingClientRect().bottom : 0;

  // Click on header / arrow -> scroll to that entry's first image
  const scrollToItem = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    setActiveId(id);
    scrollLockUntil.current = Date.now() + 900; // ignore scroll-spy while animating
    const top = window.scrollY + el.getBoundingClientRect().top - getLine();
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  
  useEffect(() => {
    if (items.length === 0) return undefined;
    let ticking = false;

    const update = () => {
      ticking = false;
      if (Date.now() < scrollLockUntil.current) return;
      const line = getLine() + 2;
      let found = items[0].id;
      for (const item of items) {
        const el = sectionRefs.current[item.id];
        if (el && el.getBoundingClientRect().top <= line) found = item.id;
      }
      setActiveId((prev) => (prev === found ? prev : found));
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [items.length]);

 
  useEffect(() => {
    const list = navListRef.current;
    if (!list || currentId === null) return;
    const btn = list.querySelector("button.active");
    if (!btn) return;
    const target = btn.offsetLeft - (list.clientWidth - btn.offsetWidth) / 2;
    list.scrollTo({ left: target, behavior: "smooth" });
  }, [currentId]);

  
  const [navEdge, setNavEdge] = useState({ left: false, right: false });

  useEffect(() => {
    const list = navListRef.current;
    if (!list) return undefined;

    const updateEdges = () => {
      const left = list.scrollLeft > 4;
      const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 4;
      setNavEdge((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };

    updateEdges();
    list.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      list.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, [items.length]);

  const scrollNav = (direction) => {
    const list = navListRef.current;
    if (!list) return;
    list.scrollBy({ left: direction * list.clientWidth * 0.6, behavior: "smooth" });
  };

  const goToStep = (step) => {
    if (currentIndex === -1) return;
    const next = (currentIndex + step + items.length) % items.length;
    scrollToItem(items[next].id);
  };

  if (items.length === 0) {
    return (
      <section className="Eyeq_section">
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            padding: "40px",
          }}
        >
          Data not available
        </div>
      </section>
    );
  }

  const prevItem = items[(currentIndex - 1 + items.length) % items.length];
  const nextItem = items[(currentIndex + 1) % items.length];

  return (
    <section className="Eyeq_section">
      {/* ---------- TOP HEADER (master names) ---------- */}
      <div className="eyeq_sticky_header" ref={headerRef}>
        <nav className="eyeq_nav">
          <button
            type="button"
            className={`eyeq_nav_scroll left${navEdge.left ? " show" : ""}`}
            aria-label="Scroll menu left"
            tabIndex={navEdge.left ? 0 : -1}
            onClick={() => scrollNav(-1)}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <ul ref={navListRef}>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={currentId === item.id ? "active" : ""}
                  onClick={() => scrollToItem(item.id)}
                >
                  {item.name}
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={`eyeq_nav_scroll right${navEdge.right ? " show" : ""}`}
            aria-label="Scroll menu right"
            tabIndex={navEdge.right ? 0 : -1}
            onClick={() => scrollNav(1)}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </nav>
      </div>
      <div aria-hidden="true" style={{ height: headerHeight }} />

      {/* ---------- PREV / NEXT ARROWS ---------- */}
      {items.length > 1 && (
        <>
          <button
            type="button"
            className="eyeq_arrow prev"
            aria-label={`Previous: ${prevItem.name}`}
            data-tooltip={`Previous: ${prevItem.name}`}
            onClick={() => goToStep(-1)}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="eyeq_arrow next"
            aria-label={`Next: ${nextItem.name}`}
            data-tooltip={`Next: ${nextItem.name}`}
            onClick={() => goToStep(1)}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* ---------- ALL IMAGES */}
      <div className="eyeq_pages">
        {items.map((item, itemIndex) => (
          <div
            key={item.id}
            className="eyeq_item"
            ref={(el) => {
              sectionRefs.current[item.id] = el;
            }}
          >
            {getImages(item).map((img, i) => (
              <img
                key={`${item.id}-${i}`}
                className="eyeq_page_img"
                src={process.env.REACT_APP_IMAGE_URL + img}
                alt={`${item.name} ${i + 1}`}
                loading={itemIndex === 0 && i === 0 ? "eager" : "lazy"}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export default Eyeq;